import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import { ModalOpenButton } from "@msys/ui";
import { HelpOutlined as DecisionIcon } from "@mui/icons-material";
import { Button, ButtonProps, SvgIcon } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ComponentProps } from "react";
import {
  TemplateSearchSortingInput,
  TplApplicableFor,
} from "../../../../clients/graphqlTypes.js";
import type { TreeItemTemplatePlaceholderFragment } from "../Fragments.generated.js";
import { TemplateSelectModal } from "../../templates/quote/TemplateSelectModal.js";
import { useSetItemTemplatePlaceholderTemplateMutation } from "./TemplatePlaceholderSelectTemplateButton.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  templateTypeName: string;
  templateSearchFilterDefaults: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchFilterDefaults"],
    "__typename"
  >;
  templateSearchSortingDefaults: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchSortingDefaults"],
    "__typename"
  >;
  templateApplicableFor: TplApplicableFor;
  onCompleted?: (itemId: string) => void;
  type?: "button" | "icon";
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  label?: string;
  disabled?: boolean;
  Icon?: typeof SvgIcon;
  refetchWizardQuestions: () => Promise<unknown>;
}

export const TemplatePlaceholderSelectTemplateButton = ({
  projectId,
  docId,
  itemId,
  templateTypeName,
  templateSearchFilterDefaults,
  templateSearchSortingDefaults,
  templateApplicableFor,
  onCompleted,
  type = "button",
  color = "secondary",
  variant = "contained",
  size = "small",
  label,
  disabled,
  Icon = DecisionIcon,
  refetchWizardQuestions,
}: Props) => {
  const { t } = useTranslate(["Decisions"]);

  const client = useApolloClient();
  const [setItemTemplatePlaceholderTemplateMutation] =
    useSetItemTemplatePlaceholderTemplateMutation({
      client,
      onCompleted: ({
        setItemTemplatePlaceholderTemplate: {
          item: { id },
        },
      }) => onCompleted?.(id),
    });

  const button =
    type === "button" ? (
      <Button
        disabled={disabled}
        color={color}
        variant={variant}
        size={size}
        startIcon={<Icon />}
        title={label ?? t("Question", { ns: "Decisions" })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {label ?? t("Question", { ns: "Decisions" })}
      </Button>
    ) : type === "icon" ? (
      <Button
        disabled={disabled}
        color={color}
        variant={variant}
        size={size}
        title={t("Question", { ns: "Decisions" })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ minWidth: 32, width: 32 }}
      >
        <Icon fontSize="small" style={{ fontSize: "1.15rem" }} />
      </Button>
    ) : (
      assertNever(type)
    );

  return (
    <ModalOpenButton
      Modal={TemplateSelectModal}
      modalProps={{
        title: t("Select a {templateTypeName}", {
          ns: "Decisions",
          templateTypeName,
        }),
        initialFilters: filterDefinitionsToFormValues(
          templateSearchFilterDefaults
        ),
        fixedFilters: {
          applicableFor: [templateApplicableFor],
          templateTypeIds: templateSearchFilterDefaults.templateTypeIdFilter
            ? [templateSearchFilterDefaults.templateTypeIdFilter.valueId]
            : null,
        },
        initialSorting: sortingDefinitionsToFormValues(
          templateSearchSortingDefaults
        ),
        handleComplete: async (template, handleClose) => {
          if (template.resolvedAsReadModelVersionNumber) {
            await setItemTemplatePlaceholderTemplateMutation({
              variables: {
                input: {
                  docId,
                  itemId,
                  projectId,
                  templateQuoteId: template.id,
                  templateQuoteIdAtVersionNumber:
                    template.resolvedAsReadModelVersionNumber,
                },
              },
            });
            await refetchWizardQuestions();
            handleClose();
          }
        },
      }}
    >
      {button}
    </ModalOpenButton>
  );
};

function filterDefinitionsToFormValues(
  definitions: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchFilterDefaults"],
    "__typename"
  >
): ComponentProps<typeof TemplateSelectModal>["initialFilters"] {
  return {
    templateTypeIds: definitions.templateTypeIdFilter
      ? [definitions.templateTypeIdFilter.valueId]
      : null,
    properties: definitions.propertyFilters.map(propertyFilter => {
      switch (propertyFilter.__typename) {
        case "EntitySearchPropertyFilterBoolFilter": {
          return {
            key: propertyFilter.key,
            boolFilter: {
              operatorBool: propertyFilter.operatorBool,
              valueBool: propertyFilter.valueBool,
            },
          };
        }
        case "EntitySearchPropertyFilterBoolInFilter": {
          return {
            key: propertyFilter.key,
            boolInFilter: {
              operatorBoolIn: propertyFilter.operatorBoolIn,
              valueBoolIn: propertyFilter.valueBoolIn,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberBetweenFilter": {
          return {
            key: propertyFilter.key,
            numberBetweenFilter: {
              operatorNumberBetween: propertyFilter.operatorNumberBetween,
              valueNumberBetween: propertyFilter.valueNumberBetween,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberFilter": {
          return {
            key: propertyFilter.key,
            numberFilter: {
              operatorNumber: propertyFilter.operatorNumber,
              valueNumber: propertyFilter.valueNumber,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberInFilter": {
          return {
            key: propertyFilter.key,
            numberInFilter: {
              operatorNumberIn: propertyFilter.operatorNumberIn,
              valueNumberIn: propertyFilter.valueNumberIn,
            },
          };
        }
        case "EntitySearchPropertyFilterTextFilter": {
          return {
            key: propertyFilter.key,
            textFilter: {
              operatorText: propertyFilter.operatorText,
              valueText: propertyFilter.valueText,
            },
          };
        }
        case "EntitySearchPropertyFilterTextInFilter": {
          return {
            key: propertyFilter.key,
            textInFilter: {
              operatorTextIn: propertyFilter.operatorTextIn,
              valueTextIn: propertyFilter.valueTextIn,
            },
          };
        }
        case "EntitySearchPropertyFilterTextArrayOfFilter": {
          return {
            key: propertyFilter.key,
            textArrayOfFilter: {
              operatorTextArrayOf: propertyFilter.operatorTextArrayOf,
              valueTextArrayOf: propertyFilter.valueTextArrayOf,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberArrayOfFilter": {
          return {
            key: propertyFilter.key,
            numberArrayOfFilter: {
              operatorNumberArrayOf: propertyFilter.operatorNumberArrayOf,
              valueNumberArrayOf: propertyFilter.valueNumberArrayOf,
            },
          };
        }
        default:
          assertNever(propertyFilter);
      }
    }),
  };
}

function sortingDefinitionsToFormValues(
  definitions: TreeItemTemplatePlaceholderFragment["templateSearchSortingDefaults"]
): TemplateSearchSortingInput[] {
  return definitions.map(definition => ({
    propertySorting:
      definition.__typename === "EntitySearchSortingPropertyValueSorting"
        ? {
            kind: definition.kind,
            key: definition.key,
            direction: definition.direction,
          }
        : undefined,
    fieldSorting: undefined,
  }));
}
